/**
 * SubscribeToPremiumPackagesLiveChangesUseCase
 *
 * Starts a Firestore `onSnapshot` listener on
 * `users/{userId}/premium_packages` and mirrors each document change into
 * SecureStorage under the `USER_PREMIUM_PACKAGES` prefix:
 *   - added/modified                       → write the local entry
 *   - added/modified with status === expired → also reset gated user settings
 *                                              to their defaultValue
 *   - removed                               → remove the local entry AND
 *                                              reset gated user settings
 *
 * Why: when an in-app-purchase / restore / cancellation cloud-function call
 * lands, the server writes the user_premium_package document. Without this
 * listener, the web client only sees that change after a full sync — the
 * SecureStorage prefix listener (`PremiumPackagesListener`) fires only on
 * local writes, so the Redux slice and any UI selectors built on it stay
 * stale until the next app load. This use case closes the loop by being the
 * source of those local writes.
 *
 * Where it runs: started exactly once, at app boot, by
 * `PremiumPackagesListener` in `presentation/components/app/` when the user
 * is authenticated and `firebaseUserId` is known. The component owns the
 * returned `Unsubscribe` and tears it down on sign-out / unmount. It is NOT
 * meant to be called from feature hooks or pages.
 *
 * Flutter parity: mirrors `PremiumPackagesService.listenToPremiumPackagesChanges`
 * (`lib/services/premium_packages_service.dart:13-70`), which Flutter starts at
 * app boot for the same reason.
 *
 * Architecture: thin wrapper over
 * `PremiumPackagesActionsService.listenToPremiumPackagesChanges`, which holds
 * the per-change SecureStorage logic. Kept as a use case so the presentation
 * layer never reaches into a domain service directly, matching
 * `subscribeToExclusiveContentLiveChangesUseCase`.
 */

import { PremiumPackagesActionsService } from '@/domain/services/premiumPackage/premiumPackagesActionsService';
import type { Unsubscribe } from 'firebase/firestore';

export class SubscribeToPremiumPackagesLiveChangesUseCase {
  constructor(private service = PremiumPackagesActionsService.getInstance()) {}

  execute(userId: string): Unsubscribe {
    return this.service.listenToPremiumPackagesChanges(userId);
  }
}

export const subscribeToPremiumPackagesLiveChangesUseCase = new SubscribeToPremiumPackagesLiveChangesUseCase();
